import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Trophy, Heart, UserPlus, Bank, Gift, Broadcast, Check, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const ICONS = {
    payout_approved: Bank,
    payout_rejected: Bank,
    follower_new: UserPlus,
    gift_received: Gift,
    tournament_win: Trophy,
    tournament_place: Trophy,
    live_guest_invite: Broadcast,
    live_guest_joined: Broadcast,
    live_guest_kicked: Broadcast,
};
const COLORS = {
    payout_approved: "text-wta-accent",
    payout_rejected: "text-wta-primary",
    follower_new: "text-wta-secondary",
    gift_received: "text-wta-accent",
    tournament_win: "text-wta-accent",
    tournament_place: "text-white",
    live_guest_invite: "text-wta-primary",
    live_guest_joined: "text-wta-primary",
    live_guest_kicked: "text-wta-primary",
};

export default function NotificationsBell() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const ref = useRef(null);

    const loadCount = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnread(data.unread);
        } catch { /* ignore */ }
    }, [user]);

    const loadItems = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications?limit=20");
            setItems(data.items);
            setUnread(data.unread);
        } catch { /* ignore */ }
    }, [user]);

    // Initial + 30s poll
    useEffect(() => {
        if (!user) { setUnread(0); setItems([]); return; }
        loadCount();
        const t = setInterval(loadCount, 30000);
        return () => clearInterval(t);
    }, [user, loadCount]);

    // Close on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const toggle = async () => {
        const willOpen = !open;
        setOpen(willOpen);
        if (willOpen) {
            await loadItems();
            // Mark all as read after opening
            try {
                await api.post("/notifications/mark-read", { ids: null });
                setUnread(0);
            } catch { /* ignore */ }
        }
    };

    const respondInvite = async (n, accept) => {
        try {
            await api.post(`/live/${n.ref_id}/guests/${accept ? "accept" : "decline"}`);
            toast.success(accept ? "You're on stage" : "Invite declined");
            // Hide the action by removing this item from the list
            setItems((arr) => arr.filter((x) => x.id !== n.id));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    if (!user) return null;

    return (
        <div ref={ref} className="relative">
            <button
                data-testid="notifications-bell"
                onClick={toggle}
                className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition"
                aria-label="Notifications"
            >
                <Bell size={20} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-wta-accent" : "text-white/80"} />
                {unread > 0 && (
                    <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-wta-primary text-white text-[10px] font-heading flex items-center justify-center">
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 md:right-auto md:left-0 mt-2 w-80 max-w-[90vw] bg-wta-surface border border-wta-border rounded-sm shadow-2xl z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-wta-border flex items-center justify-between">
                        <div className="font-heading text-lg tracking-wider">Notifications</div>
                        {items.length > 0 && (
                            <Link to="/notifications" data-testid="see-all-notifs" onClick={() => setOpen(false)} className="text-xs text-wta-muted hover:text-white">See all</Link>
                        )}
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="p-6 text-center text-wta-muted text-sm">You're all caught up.</div>
                        ) : items.slice(0, 10).map((n) => {
                            const Icon = ICONS[n.type] || Bell;
                            const color = COLORS[n.type] || "text-white";
                            const isInvite = n.type === "live_guest_invite";
                            const content = (
                                <div className="flex gap-3 px-4 py-3 hover:bg-white/5 transition border-b border-wta-border last:border-0">
                                    <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-wta-surface2 border border-wta-border ${color}`}>
                                        <Icon size={16} weight="fill" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{n.title}</div>
                                        <div className="text-xs text-wta-muted line-clamp-2">{n.message}</div>
                                        <div className="text-[10px] text-wta-muted mt-1">{new Date(n.created_at).toLocaleString()}</div>
                                        {isInvite && n.ref_id && (
                                            <div className="flex gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    data-testid={`notif-invite-accept-${n.id}`}
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); respondInvite(n, true); }}
                                                    className="flex-1 btn-primary py-1.5 rounded-sm text-xs flex items-center justify-center gap-1"
                                                >
                                                    <Check size={12} weight="bold" /> Accept
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid={`notif-invite-decline-${n.id}`}
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); respondInvite(n, false); }}
                                                    className="flex-1 btn-ghost py-1.5 rounded-sm text-xs flex items-center justify-center gap-1"
                                                >
                                                    <X size={12} weight="bold" /> Decline
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                            return n.link ? (
                                <Link key={n.id} to={n.link} data-testid={`notif-${n.id}`} onClick={() => setOpen(false)}>{content}</Link>
                            ) : (
                                <div key={n.id} data-testid={`notif-${n.id}`}>{content}</div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
